import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import InfluencerList from './components/InfluencerList';
import InfluencerProfile from './components/InfluencerProfile';
import Analytics from './components/Analytics';
import Settings from './components/Settings';
import HeepsyScraper from './components/HeepsyScraper';
import ReviewQueue from './components/ReviewQueue';
import Campaigns from './components/Campaigns';
import AdminSettings from './components/AdminSettings';
import Pipeline from './components/Pipeline';
import Login from './components/Login';
import { dashboardApi } from './services/api';

/**
 * The whole app sits behind a session check.
 *
 * Nothing — not the main route, not a detail page, not a deep link — renders
 * until /api/auth/me confirms a session. This is a convenience gate, not the
 * security boundary: the server rejects every /api request without a valid
 * session cookie, so a user who skips the UI still gets 401s.
 */
function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async () => {
    try {
      const { data } = await dashboardApi.me();
      setUser(data.user);
    } catch (err) {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  // Any 401 from any page (expired session) drops straight back to login.
  useEffect(() => {
    const onSignedOut = () => setUser(null);
    window.addEventListener('affiwise:signed-out', onSignedOut);
    return () => window.removeEventListener('affiwise:signed-out', onSignedOut);
  }, []);

  const signOut = async () => {
    try { await dashboardApi.logout(); } catch (err) { /* clearing locally is enough */ }
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) return <Login onSignedIn={setUser} />;

  return (
    <Router>
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header user={user} onSignOut={signOut} />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
            <div className="container mx-auto px-6 py-8">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/influencers" element={<InfluencerList />} />
                <Route path="/influencer/:id" element={<InfluencerProfile />} />
                <Route path="/heepsy" element={<HeepsyScraper />} />
                <Route path="/review" element={<ReviewQueue />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/admin-settings" element={<AdminSettings />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
